// ==============================================================================
// FORMULARIOS ESPECÍFICOS PARA CADA CATEGORÍA DE HEALTH DATA
// ==============================================================================

import React, { useState, useEffect } from 'react';
import { FiUser, FiCheck, FiCalendar, FiHeart, FiDroplet, FiShield, FiFilter, FiTrendingUp} from 'react-icons/fi';
import { FaBaby } from 'react-icons/fa';

// ==============================================================================
// FUNCIONES DE MAPEO CORREGIDAS PARA EDICIÓN
// ==============================================================================

// Función para mapear datos del backend al formato del formulario
const mapBackendDataToFormData = (category, backendData) => {
  if (!backendData) return {};

  switch (category) {
    case 'vitals':
      return {
        systolic: backendData.bloodPressure?.systolic || '',
        diastolic: backendData.bloodPressure?.diastolic || '',
        heartRate: backendData.heartRate || '',
        temperature: backendData.temperature || '',
        oxygenSaturation: backendData.oxygenSaturation || '',
        respiratoryRate: backendData.respiratoryRate || ''
      };
        
    case 'bodyMeasurements':
      return {
        height: backendData.height || '',
        weight: backendData.weight || '',
        headCircumference: backendData.headCircumference || ''
      };
        
    case 'sugarProfile':
      return {
        fastingBloodSugar: backendData.fastingBloodSugar || '',
        postPrandialSugar: backendData.postPrandialSugar || '',
        hba1c: backendData.hba1c || ''
      };
        
    case 'liverProfile':
      return {
        alt: backendData.alt || '',
        ast: backendData.ast || '',
        alp: backendData.alp || '',
        ggt: backendData.ggt || '',
        bilirubinTotal: backendData.bilirubinTotal || '',
        albumin: backendData.albumin || ''
      };
        
    case 'kidneyProfile':
      return {
        creatinine: backendData.creatinine || '',
        urea: backendData.urea || '',
        egfr: backendData.egfr || '',
        sodium: backendData.sodium || '',
        potassium: backendData.potassium || ''
      };
        
    case 'lipidProfile':
      return {
        totalCholesterol: backendData.totalCholesterol || '',
        ldl: backendData.ldl || '',
        hdl: backendData.hdl || '',
        triglycerides: backendData.triglycerides || ''
      };
        
    case 'cycleTracking':
      return {
        cycleEndDate: backendData.cycleEndDate || '',
        ovulationDate: backendData.ovulationDate || '',
        expectedNextPeriod: backendData.expectedNextPeriod || '',
        cycleLength: backendData.cycleLength || '',
        flowLevel: backendData.flowLevel || 'moderate',
        symptoms: Array.isArray(backendData.symptoms) ? backendData.symptoms.join(', ') : (backendData.symptoms || ''),
        mood: backendData.mood || '',
        notes: backendData.notes || ''
      };
        
    case 'pregnancyTracking':
      return {
        isPregnant: backendData.isPregnant || false,
        gestationalAgeWeeks: backendData.gestationalAgeWeeks || '',
        fetalHeartRate: backendData.fetalHeartRate || '',
        fundalHeight: backendData.fundalHeight || '',
        weight: backendData.weight || '',
        bpSystolic: backendData.bloodPressure?.systolic || '',
        bpDiastolic: backendData.bloodPressure?.diastolic || '',
        glucoseChallengeTest: backendData.glucoseChallengeTest || '',
        ultrasoundFindings: backendData.ultrasoundFindings || '',
        symptoms: Array.isArray(backendData.symptoms) ? backendData.symptoms.join(', ') : (backendData.symptoms || ''),
        medications: Array.isArray(backendData.medications) ? backendData.medications.join(', ') : (backendData.medications || ''),
        doctorNotes: backendData.doctorNotes || '',
        nextAppointment: backendData.nextAppointment || ''
      };
        
    default:
      return {};
  }
};

// ==============================================================================
// COMPONENTE UNIVERSAL PARA TODOS LOS FORMULARIOS
// ==============================================================================
const UniversalHealthForm = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  category = 'vitals',
  editData = null,
  isSubmitting = false,
  patientData 
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  // Inicializar datos del formulario
  useEffect(() => {
  if (editData && category) {
    initializeFormData(category, editData);
  } else {
    initializeFormData(category);
  }
}, [editData, category]);

  const initializeFormData = (cat, data = null) => {
  if (data) {
    // Si hay datos, mapearlos correctamente
    const mappedData = mapBackendDataToFormData(cat, data);
    setFormData(mappedData);
  } else {
    // Si no hay datos, inicializar con valores por defecto
    switch (cat) {
      case 'vitals':
        setFormData({
          systolic: '',
          diastolic: '',
          heartRate: '',
          temperature: '',
          oxygenSaturation: '',
          respiratoryRate: ''
        });
        break;
        
      case 'bodyMeasurements':
        setFormData({
          height: '',
          weight: '',
          headCircumference: ''
        });
        break;
        
      case 'sugarProfile':
        setFormData({
          fastingBloodSugar: '',
          postPrandialSugar: '',
          hba1c: ''
        });
        break;
        
      case 'liverProfile':
        setFormData({
          alt: '',
          ast: '',
          alp: '',
          ggt: '',
          bilirubinTotal: '',
          albumin: ''
        });
        break;
        
      case 'kidneyProfile':
        setFormData({
          creatinine: '',
          urea: '',
          egfr: '',
          sodium: '',
          potassium: ''
        });
        break;
        
      case 'lipidProfile':
        setFormData({
          totalCholesterol: '',
          ldl: '',
          hdl: '',
          triglycerides: ''
        });
        break;
        
      case 'cycleTracking':
        setFormData({
          cycleEndDate: '',
          ovulationDate: '',
          expectedNextPeriod: '',
          cycleLength: '',
          flowLevel: 'moderate',
          symptoms: '',
          mood: '',
          notes: ''
        });
        break;
        
      case 'pregnancyTracking':
        setFormData({
          isPregnant: false,
          gestationalAgeWeeks: '',
          fetalHeartRate: '',
          fundalHeight: '',
          weight: '',
          bpSystolic: '',
          bpDiastolic: '',
          glucoseChallengeTest: '',
          ultrasoundFindings: '',
          symptoms: '',
          medications: '',
          doctorNotes: '',
          nextAppointment: ''
        });
        break;
    }
  }
};




  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validación básica: al menos un campo debe tener datos
    const hasAnyData = Object.values(formData).some(value => 
      value !== '' && value !== null && value !== undefined && value !== false
    );
    
    if (!hasAnyData) {
      newErrors.general = `Please provide at least one ${getCategoryDisplayName(category)} measurement`;
    }
    
    // Validaciones específicas por categoría
    switch (category) {
      case 'vitals':
        if ((formData.systolic && !formData.diastolic) || (!formData.systolic && formData.diastolic)) {
          newErrors.bloodPressure = 'Please provide both systolic and diastolic readings';
        }
        break;
        
      case 'bodyMeasurements':
        // No validaciones adicionales específicas
        break;
        
      case 'cycleTracking':
        if (formData.cycleEndDate && formData.expectedNextPeriod) {
          const endDate = new Date(formData.cycleEndDate);
          const nextDate = new Date(formData.expectedNextPeriod);
          if (nextDate <= endDate) {
            newErrors.dates = 'Expected next period should be after cycle end date';
          }
        }
        break;
        
      case 'pregnancyTracking':
        if ((formData.bpSystolic && !formData.bpDiastolic) || (!formData.bpSystolic && formData.bpDiastolic)) {
          newErrors.bloodPressure = 'Please provide both systolic and diastolic readings';
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (!validateForm()) {
    return;
  }

  try {
    const cleanData = prepareDataForSubmission(category, formData);
    
    // Si editData existe, es una actualización; si no, es creación
    if (editData) {
      // Pasar el índice del registro para actualizaciones
      await onSubmit(cleanData, 'update');
    } else {
      await onSubmit(cleanData, 'create');
    }
    
    // Reset form on success
    initializeFormData(category);
  } catch (error) {
    console.error('Error submitting form:', error);
  }
};

  const prepareDataForSubmission = (cat, data) => {
    switch (cat) {
      case 'vitals':
        return {
          systolic: data.systolic ? parseInt(data.systolic) : null,
          diastolic: data.diastolic ? parseInt(data.diastolic) : null,
          heartRate: data.heartRate ? parseInt(data.heartRate) : null,
          temperature: data.temperature ? parseFloat(data.temperature) : null,
          oxygenSaturation: data.oxygenSaturation ? parseInt(data.oxygenSaturation) : null,
          respiratoryRate: data.respiratoryRate ? parseInt(data.respiratoryRate) : null
        };
        
      case 'bodyMeasurements':
        return {
          height: data.height ? parseFloat(data.height) : null,
          weight: data.weight ? parseFloat(data.weight) : null,
          headCircumference: data.headCircumference ? parseFloat(data.headCircumference) : null
        };
        
      case 'sugarProfile':
        return {
          fastingBloodSugar: data.fastingBloodSugar ? parseFloat(data.fastingBloodSugar) : null,
          postPrandialSugar: data.postPrandialSugar ? parseFloat(data.postPrandialSugar) : null,
          hba1c: data.hba1c ? parseFloat(data.hba1c) : null
        };
        
      case 'liverProfile':
        return {
          alt: data.alt ? parseFloat(data.alt) : null,
          ast: data.ast ? parseFloat(data.ast) : null,
          alp: data.alp ? parseFloat(data.alp) : null,
          ggt: data.ggt ? parseFloat(data.ggt) : null,
          bilirubinTotal: data.bilirubinTotal ? parseFloat(data.bilirubinTotal) : null,
          albumin: data.albumin ? parseFloat(data.albumin) : null
        };
        
      case 'kidneyProfile':
        return {
          creatinine: data.creatinine ? parseFloat(data.creatinine) : null,
          urea: data.urea ? parseFloat(data.urea) : null,
          egfr: data.egfr ? parseFloat(data.egfr) : null,
          sodium: data.sodium ? parseFloat(data.sodium) : null,
          potassium: data.potassium ? parseFloat(data.potassium) : null
        };
        
      case 'lipidProfile':
        return {
          totalCholesterol: data.totalCholesterol ? parseFloat(data.totalCholesterol) : null,
          ldl: data.ldl ? parseFloat(data.ldl) : null,
          hdl: data.hdl ? parseFloat(data.hdl) : null,
          triglycerides: data.triglycerides ? parseFloat(data.triglycerides) : null
        };
        
      case 'cycleTracking':
        return {
          cycleEndDate: data.cycleEndDate || null,
          ovulationDate: data.ovulationDate || null,
          expectedNextPeriod: data.expectedNextPeriod || null,
          cycleLength: data.cycleLength ? parseInt(data.cycleLength) : null,
          flowLevel: data.flowLevel || null,
          symptoms: data.symptoms ? data.symptoms.split(',').map(s => s.trim()).filter(s => s) : [],
          mood: data.mood || null,
          notes: data.notes || null
        };
        
      case 'pregnancyTracking':
        return {
          isPregnant: data.isPregnant,
          gestationalAgeWeeks: data.gestationalAgeWeeks ? parseInt(data.gestationalAgeWeeks) : null,
          fetalHeartRate: data.fetalHeartRate ? parseInt(data.fetalHeartRate) : null,
          fundalHeight: data.fundalHeight ? parseFloat(data.fundalHeight) : null,
          weight: data.weight ? parseFloat(data.weight) : null,
          bpSystolic: data.bpSystolic ? parseInt(data.bpSystolic) : null,
          bpDiastolic: data.bpDiastolic ? parseInt(data.bpDiastolic) : null,
          glucoseChallengeTest: data.glucoseChallengeTest ? parseFloat(data.glucoseChallengeTest) : null,
          ultrasoundFindings: data.ultrasoundFindings || null,
          symptoms: data.symptoms ? data.symptoms.split(',').map(s => s.trim()).filter(s => s) : [],
          medications: data.medications ? data.medications.split(',').map(s => s.trim()).filter(s => s) : [],
          doctorNotes: data.doctorNotes || null,
          nextAppointment: data.nextAppointment || null
        };
        
      default:
        return data;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="offcanvas offcanvas-end show" style={{ visibility: 'visible', zIndex: 1050, width: '600px' }}>
      <div className="offcanvas-header">
        <h5 className="offcanvas-title">
          {getCategoryIcon(category)}
          {editData ? 'Edit' : 'Add New'} {getCategoryDisplayName(category)}
        </h5>
        <button
          type="button"
          className="btn-close"
          onClick={onClose}
          disabled={isSubmitting}
        />
      </div>
      <div className="offcanvas-body">
        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            {/* Patient Info */}
            <div className="col-12">
              <div className="alert alert-info">
                <FiUser className="me-2" />
                {editData ? 'Editing' : 'Recording'} {getCategoryDisplayName(category).toLowerCase()} for: <strong>{patientData?.patientName}</strong>
              </div>
            </div>

            {/* General Error */}
            {errors.general && (
              <div className="col-12">
                <div className="alert alert-danger">{errors.general}</div>
              </div>
            )}

            {/* Render specific form fields based on category */}
            {renderFormFields(category, formData, handleInputChange, errors, isSubmitting)}

            {/* Action Buttons */}
            <div className="col-12">
              <div className="d-flex gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      {editData ? 'Updating...' : 'Recording...'}
                    </>
                  ) : (
                    <>
                      <FiCheck className="me-2" />
                      {editData ? 'Update' : 'Record'} {getCategoryDisplayName(category)}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==============================================================================
// FUNCIONES DE RENDERIZADO DE CAMPOS ESPECÍFICOS POR CATEGORÍA
// ==============================================================================

const renderFormFields = (category, formData, handleInputChange, errors, isSubmitting) => {
  switch (category) {
    case 'vitals':
      return renderVitalsFields(formData, handleInputChange, errors, isSubmitting);
    case 'bodyMeasurements':
      return renderBodyMeasurementsFields(formData, handleInputChange, errors, isSubmitting);
    case 'sugarProfile':
      return renderSugarProfileFields(formData, handleInputChange, errors, isSubmitting);
    case 'liverProfile':
      return renderLiverProfileFields(formData, handleInputChange, errors, isSubmitting);
    case 'kidneyProfile':
      return renderKidneyProfileFields(formData, handleInputChange, errors, isSubmitting);
    case 'lipidProfile':
      return renderLipidProfileFields(formData, handleInputChange, errors, isSubmitting);
    case 'cycleTracking':
      return renderCycleTrackingFields(formData, handleInputChange, errors, isSubmitting);
    case 'pregnancyTracking':
      return renderPregnancyTrackingFields(formData, handleInputChange, errors, isSubmitting);
    default:
      return null;
  }
};

// VITAL SIGNS FIELDS
const renderVitalsFields = (formData, handleInputChange, errors, isSubmitting) => (
  <>
    {/* Blood Pressure */}
    <div className="col-12">
      <label className="form-label">Blood Pressure (mmHg)</label>
      <div className="row">
        <div className="col-6">
          <input
            type="number"
            className={`form-control ${errors.bloodPressure ? 'is-invalid' : ''}`}
            placeholder="Systolic"
            value={formData.systolic}
            onChange={(e) => handleInputChange('systolic', e.target.value)}
            disabled={isSubmitting}
            min="0"
            max="300"
          />
        </div>
        <div className="col-6">
          <input
            type="number"
            className={`form-control ${errors.bloodPressure ? 'is-invalid' : ''}`}
            placeholder="Diastolic"
            value={formData.diastolic}
            onChange={(e) => handleInputChange('diastolic', e.target.value)}
            disabled={isSubmitting}
            min="0"
            max="200"
          />
        </div>
      </div>
      {errors.bloodPressure && <div className="invalid-feedback d-block">{errors.bloodPressure}</div>}
    </div>

    {/* Heart Rate */}
    <div className="col-md-6">
      <label className="form-label">Heart Rate (bpm)</label>
      <input
        type="number"
        className="form-control"
        value={formData.heartRate}
        onChange={(e) => handleInputChange('heartRate', e.target.value)}
        placeholder="e.g., 72"
        disabled={isSubmitting}
        min="0"
        max="300"
      />
    </div>

    {/* Temperature */}
    <div className="col-md-6">
      <label className="form-label">Temperature (°C)</label>
      <input
        type="number"
        step="0.1"
        className="form-control"
        value={formData.temperature}
        onChange={(e) => handleInputChange('temperature', e.target.value)}
        placeholder="e.g., 36.5"
        disabled={isSubmitting}
        min="30"
        max="45"
      />
    </div>

    {/* Oxygen Saturation */}
    <div className="col-md-6">
      <label className="form-label">Oxygen Saturation (%)</label>
      <input
        type="number"
        className="form-control"
        value={formData.oxygenSaturation}
        onChange={(e) => handleInputChange('oxygenSaturation', e.target.value)}
        placeholder="e.g., 98"
        disabled={isSubmitting}
        min="0"
        max="100"
      />
    </div>

    {/* Respiratory Rate */}
    <div className="col-md-6">
      <label className="form-label">Respiratory Rate (/min)</label>
      <input
        type="number"
        className="form-control"
        value={formData.respiratoryRate}
        onChange={(e) => handleInputChange('respiratoryRate', e.target.value)}
        placeholder="e.g., 16"
        disabled={isSubmitting}
        min="0"
        max="100"
      />
    </div>
  </>
);

// BODY MEASUREMENTS FIELDS
const renderBodyMeasurementsFields = (formData, handleInputChange, errors, isSubmitting) => (
  <>
    {/* Height */}
    <div className="col-md-6">
      <label className="form-label">Height (cm)</label>
      <input
        type="number"
        step="0.1"
        className="form-control"
        value={formData.height}
        onChange={(e) => handleInputChange('height', e.target.value)}
        placeholder="e.g., 170.5"
        disabled={isSubmitting}
        min="0"
        max="300"
      />
    </div>

    {/* Weight */}
    <div className="col-md-6">
      <label className="form-label">Weight (kg)</label>
      <input
        type="number"
        step="0.1"
        className="form-control"
        value={formData.weight}
        onChange={(e) => handleInputChange('weight', e.target.value)}
        placeholder="e.g., 70.5"
        disabled={isSubmitting}
        min="0"
        max="500"
      />
    </div>

    {/* BMI Display (calculated) */}
    {formData.height && formData.weight && (
      <div className="col-12">
        <div className="alert alert-info">
          <strong>BMI:</strong> {((formData.weight / Math.pow(formData.height/100, 2)).toFixed(1))}
          {' '}({getBMICategory(formData.weight / Math.pow(formData.height/100, 2))})
        </div>
      </div>
    )}

    {/* Head Circumference */}
    <div className="col-md-6">
      <label className="form-label">Head Circumference (cm) <small className="text-muted">(optional)</small></label>
      <input
        type="number"
        step="0.1"
        className="form-control"
        value={formData.headCircumference}
        onChange={(e) => handleInputChange('headCircumference', e.target.value)}
        placeholder="e.g., 35.0"
        disabled={isSubmitting}
        min="0"
        max="100"
      />
    </div>
  </>
);

// SUGAR PROFILE FIELDS
const renderSugarProfileFields = (formData, handleInputChange, errors, isSubmitting) => (
  <>
    {/* Fasting Blood Sugar */}
    <div className="col-md-6">
      <label className="form-label">Fasting Blood Sugar (mg/dL)</label>
      <input
        type="number"
        className="form-control"
        value={formData.fastingBloodSugar}
        onChange={(e) => handleInputChange('fastingBloodSugar', e.target.value)}
        placeholder="e.g., 95"
        disabled={isSubmitting}
        min="0"
        max="600"
      />
    </div>

    {/* Postprandial Sugar */}
    <div className="col-md-6">
      <label className="form-label">Post-meal Sugar (mg/dL)</label>
      <input
        type="number"
        className="form-control"
        value={formData.postPrandialSugar}
        onChange={(e) => handleInputChange('postPrandialSugar', e.target.value)}
        placeholder="e.g., 140"
        disabled={isSubmitting}
        min="0"
        max="600"
      />
    </div>

    {/* HbA1c */}
    <div className="col-md-6">
      <label className="form-label">HbA1c (%)</label>
      <input
        type="number"
        step="0.1"
        className="form-control"
        value={formData.hba1c}
        onChange={(e) => handleInputChange('hba1c', e.target.value)}
        placeholder="e.g., 5.7"
        disabled={isSubmitting}
        min="0"
        max="20"
      />
    </div>
  </>
);

// LIVER PROFILE FIELDS
const renderLiverProfileFields = (formData, handleInputChange, errors, isSubmitting) => (
  <>
    {/* ALT */}
    <div className="col-md-6">
      <label className="form-label">ALT (U/L)</label>
      <input
        type="number"
        className="form-control"
        value={formData.alt}
        onChange={(e) => handleInputChange('alt', e.target.value)}
        placeholder="e.g., 25"
        disabled={isSubmitting}
        min="0"
        max="1000"
      />
    </div>

    {/* AST */}
    <div className="col-md-6">
      <label className="form-label">AST (U/L)</label>
      <input
        type="number"
        className="form-control"
        value={formData.ast}
        onChange={(e) => handleInputChange('ast', e.target.value)}
        placeholder="e.g., 30"
        disabled={isSubmitting}
        min="0"
        max="1000"
      />
    </div>

    {/* ALP */}
    <div className="col-md-6">
      <label className="form-label">ALP (U/L)</label>
      <input
        type="number"
        className="form-control"
        value={formData.alp}
        onChange={(e) => handleInputChange('alp', e.target.value)}
        placeholder="e.g., 75"
        disabled={isSubmitting}
        min="0"
        max="1000"
      />
    </div>

    {/* GGT */}
    <div className="col-md-6">
      <label className="form-label">GGT (U/L) <small className="text-muted">(optional)</small></label>
      <input
        type="number"
        className="form-control"
        value={formData.ggt}
        onChange={(e) => handleInputChange('ggt', e.target.value)}
        placeholder="e.g., 20"
        disabled={isSubmitting}
        min="0"
        max="1000"
      />
    </div>

    {/* Bilirubin Total */}
    <div className="col-md-6">
      <label className="form-label">Total Bilirubin (mg/dL)</label>
      <input
        type="number"
        step="0.1"
        className="form-control"
        value={formData.bilirubinTotal}
        onChange={(e) => handleInputChange('bilirubinTotal', e.target.value)}
        placeholder="e.g., 1.0"
        disabled={isSubmitting}
        min="0"
        max="50"
      />
    </div>

    {/* Albumin */}
    <div className="col-md-6">
      <label className="form-label">Albumin (g/dL)</label>
      <input
        type="number"
        step="0.1"
        className="form-control"
        value={formData.albumin}
        onChange={(e) => handleInputChange('albumin', e.target.value)}
        placeholder="e.g., 4.2"
        disabled={isSubmitting}
        min="0"
        max="10"
      />
    </div>
  </>
);

// KIDNEY PROFILE FIELDS
const renderKidneyProfileFields = (formData, handleInputChange, errors, isSubmitting) => (
  <>
    {/* Creatinine */}
    <div className="col-md-6">
      <label className="form-label">Creatinine (mg/dL)</label>
      <input
        type="number"
        step="0.1"
        className="form-control"
        value={formData.creatinine}
        onChange={(e) => handleInputChange('creatinine', e.target.value)}
        placeholder="e.g., 1.0"
        disabled={isSubmitting}
        min="0"
        max="20"
      />
    </div>

    {/* Urea */}
    <div className="col-md-6">
      <label className="form-label">Urea (mg/dL)</label>
      <input
        type="number"
        className="form-control"
        value={formData.urea}
        onChange={(e) => handleInputChange('urea', e.target.value)}
        placeholder="e.g., 15"
        disabled={isSubmitting}
        min="0"
        max="200"
      />
    </div>

    {/* eGFR */}
    <div className="col-md-6">
      <label className="form-label">eGFR (mL/min/1.73m²)</label>
      <input
        type="number"
        className="form-control"
        value={formData.egfr}
        onChange={(e) => handleInputChange('egfr', e.target.value)}
        placeholder="e.g., 90"
        disabled={isSubmitting}
        min="0"
        max="200"
      />
    </div>

    {/* Sodium */}
    <div className="col-md-6">
      <label className="form-label">Sodium (mEq/L)</label>
      <input
        type="number"
        className="form-control"
        value={formData.sodium}
        onChange={(e) => handleInputChange('sodium', e.target.value)}
        placeholder="e.g., 140"
        disabled={isSubmitting}
        min="0"
        max="200"
      />
    </div>

    {/* Potassium */}
    <div className="col-md-6">
      <label className="form-label">Potassium (mEq/L)</label>
      <input
        type="number"
        step="0.1"
        className="form-control"
        value={formData.potassium}
        onChange={(e) => handleInputChange('potassium', e.target.value)}
        placeholder="e.g., 4.0"
        disabled={isSubmitting}
        min="0"
        max="10"
      />
    </div>
  </>
);

// CONTINUACIÓN DEL CÓDIGO - LIPID PROFILE FIELDS (completar)
const renderLipidProfileFields = (formData, handleInputChange, errors, isSubmitting) => (
  <>
    {/* Total Cholesterol */}
    <div className="col-md-6">
      <label className="form-label">Total Cholesterol (mg/dL)</label>
      <input
        type="number"
        className="form-control"
        value={formData.totalCholesterol}
        onChange={(e) => handleInputChange('totalCholesterol', e.target.value)}
        placeholder="e.g., 180"
        disabled={isSubmitting}
        min="0"
        max="500"
      />
    </div>

    {/* LDL */}
    <div className="col-md-6">
      <label className="form-label">LDL Cholesterol (mg/dL)</label>
      <input
        type="number"
        className="form-control"
        value={formData.ldl}
        onChange={(e) => handleInputChange('ldl', e.target.value)}
        placeholder="e.g., 100"
        disabled={isSubmitting}
        min="0"
        max="300"
      />
    </div>

    {/* HDL */}
    <div className="col-md-6">
      <label className="form-label">HDL Cholesterol (mg/dL)</label>
      <input
        type="number"
        className="form-control"
        value={formData.hdl}
        onChange={(e) => handleInputChange('hdl', e.target.value)}
        placeholder="e.g., 45"
        disabled={isSubmitting}
        min="0"
        max="100"
      />
    </div>

    {/* Triglycerides */}
    <div className="col-md-6">
      <label className="form-label">Triglycerides (mg/dL)</label>
      <input
        type="number"
        className="form-control"
        value={formData.triglycerides}
        onChange={(e) => handleInputChange('triglycerides', e.target.value)}
        placeholder="e.g., 120"
        disabled={isSubmitting}
        min="0"
        max="1000"
      />
    </div>

    {/* Cholesterol Risk Assessment */}
    {formData.totalCholesterol && formData.hdl && (
      <div className="col-12">
        <div className="alert alert-info">
          <strong>Cholesterol Ratio:</strong> {(formData.totalCholesterol / formData.hdl).toFixed(1)}
          {' '}({getCholesterolRiskCategory(formData.totalCholesterol / formData.hdl)})
        </div>
      </div>
    )}
  </>
);

// CYCLE TRACKING FIELDS
const renderCycleTrackingFields = (formData, handleInputChange, errors, isSubmitting) => (
  <>
    {/* Cycle End Date */}
    <div className="col-md-6">
      <label className="form-label">Last Cycle End Date</label>
      <input
        type="date"
        className={`form-control ${errors.dates ? 'is-invalid' : ''}`}
        value={formData.cycleEndDate}
        onChange={(e) => handleInputChange('cycleEndDate', e.target.value)}
        disabled={isSubmitting}
      />
    </div>

    {/* Ovulation Date */}
    <div className="col-md-6">
      <label className="form-label">Ovulation Date <small className="text-muted">(estimated)</small></label>
      <input
        type="date"
        className="form-control"
        value={formData.ovulationDate}
        onChange={(e) => handleInputChange('ovulationDate', e.target.value)}
        disabled={isSubmitting}
      />
    </div>

    {/* Expected Next Period */}
    <div className="col-md-6">
      <label className="form-label">Expected Next Period</label>
      <input
        type="date"
        className={`form-control ${errors.dates ? 'is-invalid' : ''}`}
        value={formData.expectedNextPeriod}
        onChange={(e) => handleInputChange('expectedNextPeriod', e.target.value)}
        disabled={isSubmitting}
      />
    </div>

    {/* Cycle Length */}
    <div className="col-md-6">
      <label className="form-label">Cycle Length (days)</label>
      <input
        type="number"
        className="form-control"
        value={formData.cycleLength}
        onChange={(e) => handleInputChange('cycleLength', e.target.value)}
        placeholder="e.g., 28"
        disabled={isSubmitting}
        min="20"
        max="45"
      />
    </div>

    {errors.dates && <div className="col-12"><div className="invalid-feedback d-block">{errors.dates}</div></div>}

    {/* Flow Level */}
    <div className="col-md-6">
      <label className="form-label">Flow Level</label>
      <select
        className="form-select"
        value={formData.flowLevel}
        onChange={(e) => handleInputChange('flowLevel', e.target.value)}
        disabled={isSubmitting}
      >
        <option value="light">Light</option>
        <option value="moderate">Moderate</option>
        <option value="heavy">Heavy</option>
        <option value="very_heavy">Very Heavy</option>
      </select>
    </div>

    {/* Mood */}
    <div className="col-md-6">
      <label className="form-label">Mood</label>
      <select
        className="form-select"
        value={formData.mood}
        onChange={(e) => handleInputChange('mood', e.target.value)}
        disabled={isSubmitting}
      >
        <option value="">Select mood</option>
        <option value="happy">Happy</option>
        <option value="neutral">Neutral</option>
        <option value="irritable">Irritable</option>
        <option value="sad">Sad</option>
        <option value="anxious">Anxious</option>
        <option value="energetic">Energetic</option>
        <option value="tired">Tired</option>
      </select>
    </div>

    {/* Symptoms */}
    <div className="col-12">
      <label className="form-label">Symptoms <small className="text-muted">(comma-separated)</small></label>
      <input
        type="text"
        className="form-control"
        value={formData.symptoms}
        onChange={(e) => handleInputChange('symptoms', e.target.value)}
        placeholder="e.g., cramps, headache, bloating"
        disabled={isSubmitting}
      />
    </div>

    {/* Notes */}
    <div className="col-12">
      <label className="form-label">Notes <small className="text-muted">(optional)</small></label>
      <textarea
        className="form-control"
        rows="3"
        value={formData.notes}
        onChange={(e) => handleInputChange('notes', e.target.value)}
        placeholder="Additional notes about your cycle..."
        disabled={isSubmitting}
      />
    </div>
  </>
);

// PREGNANCY TRACKING FIELDS
const renderPregnancyTrackingFields = (formData, handleInputChange, errors, isSubmitting) => (
  <>
    {/* Pregnancy Status */}
    <div className="col-12">
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id="isPregnant"
          checked={formData.isPregnant}
          onChange={(e) => handleInputChange('isPregnant', e.target.checked)}
          disabled={isSubmitting}
        />
        <label className="form-check-label" htmlFor="isPregnant">
          Currently Pregnant
        </label>
      </div>
    </div>

    {formData.isPregnant && (
      <>
        {/* Gestational Age */}
        <div className="col-md-6">
          <label className="form-label">Gestational Age (weeks)</label>
          <input
            type="number"
            className="form-control"
            value={formData.gestationalAgeWeeks}
            onChange={(e) => handleInputChange('gestationalAgeWeeks', e.target.value)}
            placeholder="e.g., 20"
            disabled={isSubmitting}
            min="1"
            max="42"
          />
        </div>

        {/* Fetal Heart Rate */}
        <div className="col-md-6">
          <label className="form-label">Fetal Heart Rate (bpm)</label>
          <input
            type="number"
            className="form-control"
            value={formData.fetalHeartRate}
            onChange={(e) => handleInputChange('fetalHeartRate', e.target.value)}
            placeholder="e.g., 140"
            disabled={isSubmitting}
            min="100"
            max="200"
          />
        </div>

        {/* Fundal Height */}
        <div className="col-md-6">
          <label className="form-label">Fundal Height (cm)</label>
          <input
            type="number"
            step="0.1"
            className="form-control"
            value={formData.fundalHeight}
            onChange={(e) => handleInputChange('fundalHeight', e.target.value)}
            placeholder="e.g., 20.5"
            disabled={isSubmitting}
            min="0"
            max="50"
          />
        </div>
      </>
    )}

    {/* Weight */}
    <div className="col-md-6">
      <label className="form-label">Weight (kg)</label>
      <input
        type="number"
        step="0.1"
        className="form-control"
        value={formData.weight}
        onChange={(e) => handleInputChange('weight', e.target.value)}
        placeholder="e.g., 65.5"
        disabled={isSubmitting}
        min="0"
        max="200"
      />
    </div>

    {/* Blood Pressure */}
    <div className="col-12">
      <label className="form-label">Blood Pressure (mmHg)</label>
      <div className="row">
        <div className="col-6">
          <input
            type="number"
            className={`form-control ${errors.bloodPressure ? 'is-invalid' : ''}`}
            placeholder="Systolic"
            value={formData.bpSystolic}
            onChange={(e) => handleInputChange('bpSystolic', e.target.value)}
            disabled={isSubmitting}
            min="0"
            max="300"
          />
        </div>
        <div className="col-6">
          <input
            type="number"
            className={`form-control ${errors.bloodPressure ? 'is-invalid' : ''}`}
            placeholder="Diastolic"
            value={formData.bpDiastolic}
            onChange={(e) => handleInputChange('bpDiastolic', e.target.value)}
            disabled={isSubmitting}
            min="0"
            max="200"
          />
        </div>
      </div>
      {errors.bloodPressure && <div className="invalid-feedback d-block">{errors.bloodPressure}</div>}
    </div>

    {formData.isPregnant && (
      <>
        {/* Glucose Challenge Test */}
        <div className="col-md-6">
          <label className="form-label">Glucose Challenge Test (mg/dL) <small className="text-muted">(optional)</small></label>
          <input
            type="number"
            className="form-control"
            value={formData.glucoseChallengeTest}
            onChange={(e) => handleInputChange('glucoseChallengeTest', e.target.value)}
            placeholder="e.g., 140"
            disabled={isSubmitting}
            min="0"
            max="400"
          />
        </div>

        {/* Ultrasound Findings */}
        <div className="col-12">
          <label className="form-label">Ultrasound Findings <small className="text-muted">(optional)</small></label>
          <textarea
            className="form-control"
            rows="3"
            value={formData.ultrasoundFindings}
            onChange={(e) => handleInputChange('ultrasoundFindings', e.target.value)}
            placeholder="Ultrasound results and observations..."
            disabled={isSubmitting}
          />
        </div>
      </>
    )}

    {/* Symptoms */}
    <div className="col-12">
      <label className="form-label">Symptoms <small className="text-muted">(comma-separated)</small></label>
      <input
        type="text"
        className="form-control"
        value={formData.symptoms}
        onChange={(e) => handleInputChange('symptoms', e.target.value)}
        placeholder="e.g., nausea, fatigue, back pain"
        disabled={isSubmitting}
      />
    </div>

    {/* Medications */}
    <div className="col-12">
      <label className="form-label">Current Medications <small className="text-muted">(comma-separated)</small></label>
      <input
        type="text"
        className="form-control"
        value={formData.medications}
        onChange={(e) => handleInputChange('medications', e.target.value)}
        placeholder="e.g., prenatal vitamins, iron supplements"
        disabled={isSubmitting}
      />
    </div>

    {/* Doctor Notes */}
    <div className="col-12">
      <label className="form-label">Doctor Notes <small className="text-muted">(optional)</small></label>
      <textarea
        className="form-control"
        rows="3"
        value={formData.doctorNotes}
        onChange={(e) => handleInputChange('doctorNotes', e.target.value)}
        placeholder="Medical provider notes and recommendations..."
        disabled={isSubmitting}
      />
    </div>

    {/* Next Appointment */}
    <div className="col-md-6">
      <label className="form-label">Next Appointment <small className="text-muted">(optional)</small></label>
      <input
        type="date"
        className="form-control"
        value={formData.nextAppointment}
        onChange={(e) => handleInputChange('nextAppointment', e.target.value)}
        disabled={isSubmitting}
      />
    </div>
  </>
);

// ==============================================================================
// FUNCIONES AUXILIARES
// ==============================================================================

const getCategoryDisplayName = (category) => {
  const names = {
    vitals: 'Vital Signs',
    bodyMeasurements: 'Body Measurements',
    sugarProfile: 'Sugar Profile',
    liverProfile: 'Liver Profile',
    kidneyProfile: 'Kidney Profile',
    lipidProfile: 'Lipid Profile',
    cycleTracking: 'Cycle Tracking',
    pregnancyTracking: 'Pregnancy Tracking'
  };
  return names[category] || category;
};

const getCategoryIcon = (category) => {
  const icons = {
    vitals: <FiHeart className="me-2" />,
    bodyMeasurements: <FiTrendingUp className="me-2" />,
    sugarProfile: <FiDroplet className="me-2" />,
    liverProfile: <FiShield className="me-2" />,
    kidneyProfile: <FiFilter className="me-2" />,
    lipidProfile: <FiDroplet className="me-2" />,
    cycleTracking: <FiCalendar className="me-2" />,
    pregnancyTracking: <FaBaby className="me-2" />
  };
  return icons[category] || <FiUser className="me-2" />;
};

const getBMICategory = (bmi) => {
  if (bmi < 18.5) return 'Underweight';
  if (bmi < 25) return 'Normal';
  if (bmi < 30) return 'Overweight';
  return 'Obese';
};

const getCholesterolRiskCategory = (ratio) => {
  if (ratio < 3.5) return 'Low Risk';
  if (ratio < 5) return 'Moderate Risk';
  return 'High Risk';
};

export default UniversalHealthForm;